import { useState, useEffect } from 'react';
import axios from 'axios';
import { useGlobal } from '@/context/GlobalContext';

/**
 * Custom hook to fetch page content from the CMS
 * @param {string} page - The page identifier (e.g., 'home', 'mobility', 'surveillance')
 * @returns {object} - { content, loading, error, refetch }
 */
export const usePageContent = (page) => {
  const { API } = useGlobal();
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/page-content/${page}`);
      setContent(response.data || {});
      setError(null);
    } catch (err) {
      console.error(`Error fetching ${page} content:`, err);
      setError(err);
      // Keep existing content on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [page, API]);

  return { content, loading, error, refetch: fetchContent };
};

/**
 * Helper to get nested content with fallback
 * @param {object} content - The content object
 * @param {string} path - Dot-notation path (e.g., 'hero.headline')
 * @param {any} fallback - Fallback value if path doesn't exist
 */
export const getContent = (content, path, fallback = '') => {
  const keys = path.split('.');
  let result = content;
  
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = result[key];
    } else {
      return fallback;
    }
  }
  
  return result ?? fallback;
};

export default usePageContent;
